<template>
    <div class="page-wrapper">
        <div class="search-container">
            <div class="search">
                <Breadcrumb />
                <div class="container-fluid">
                    <form class="search-form">
                        <div class="form-title hide-on-mobile">
                            <h1>Suche</h1>
                        </div>
                        <div class="search-controls">
                            <div class="form-element search-element">
                                <input type="text" placeholder="Suche..." aria-label="Suchwort eingeben"
                                    class="search-icon" v-model="tempSearchWord" @keyup.enter="handleSearch">
                            </div>
                            <button type="submit" class="search-btn btn btn-fill" alt="Suchen" @click="handleSearch"
                                :disabled="isSearching">
                                <i class="material-icons" aria-hidden="true">search</i>
                                <span class="hide-on-mobile" v-if="!isSearching">Suchen</span>
                                <span class="hide-on-mobile" v-else>Wird gesucht...</span>
                            </button>
                            <button type="button" @click="resetSearch" class="reset-btn" v-if="searchWord"
                                title="Suche zurücksetzen">
                                <i class="material-icons" aria-hidden="true">clear</i>
                                <span class="hide-on-mobile">Zurücksetzen</span>
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <main class="main-content">
                <div class="container-fluid blank" v-if="!searchWord || (hasResults == 0 && !isSearching)">
                    <div class="blank-section">
                        <img src="/assets/blank-image.png" class="search-img" alt="Blank">
                        <p v-if="!searchWord">Bitte gib einen Suchbegriff ein oder wähle einen Filter aus.</p>
                        <p v-else>
                            <span class="highlight">{{ searchWord }}</span> ergab leider keine Treffer.
                            <br>
                            Bitte versuche es mit einem anderen Suchbegriff.
                        </p>
                    </div>
                </div>

                <div class="results-container" v-else>
                    <ContentSection title="Anbieter" :searchWord="searchWord" @searchResults="handleSearchResults">
                    </ContentSection>
                    <ContentSection title="Veranstaltungen" :searchWord="searchWord"
                        @searchResults="handleSearchResults">
                    </ContentSection>
                    <ContentSection title="Sehenswürdigkeiten" :searchWord="searchWord"
                        @searchResults="handleSearchResults">
                    </ContentSection>
                    <ContentSection title="KulTouren" :searchWord="searchWord" @searchResults="handleSearchResults">
                    </ContentSection>
                </div>
            </main>
        </div>
        <div class="footer-margin"></div>
    </div>
</template>

<script>
import Breadcrumb from '@/components/controls/Breadcrumb.vue';
import CardEvent from '@/components/cards/CardEvent.vue';
import CardInstitution from '@/components/cards/CardInstitution.vue';
import ContentSection from './components/ContentSection.vue';
import CardKultour from '@/components/cards/CardKultour.vue';
import Search from '@/components/Search.vue';
import { showLoader, hideLoader, isset } from '@/utils/helpers';
import { search } from '@/api/content';
import { template } from 'lodash';

export default {
    name: 'Suche',
    components: {
        CardEvent,
        CardInstitution,
        Breadcrumb,
        Search,
        ContentSection,
        CardKultour,
    },
    data() {
        return {
            loader: null,
            tempSearchWord: '',
            searchWord: '',
            timeout: null,
            hasResults: false,
            currentSearchId: 0,
            searchResultsCounter: 0,
            isSearching: false,
        }
    },
    computed: {
        uriKeyword() {
            return this.$route.query.keyword;
        },
        shouldShowBlankMessage() {
            return !this.searchWord || this.hasResults == 0;
        }
    },
    watch: {
        searchResultsCounter: {
            handler() {
                if (this.searchResultsCounter === 4) {
                    this.isSearching = false;
                    this.searchResultsCounter = 0;
                    this.$nextTick(() => {
                        try {
                            const signature = this.getCurrentFilterSignature();
                            const centralKey = `lastVisited:${this.$route.path}:search:${signature}`;
                            const payload = window.sessionStorage.getItem(centralKey);
                            if (payload) {
                                const [section, idStr] = payload.split('|');
                                const id = Number(idStr);
                                if (section && id) {
                                    let attempts = 0;
                                    const targetId = `search-${section}-${id}`;
                                    const tryScroll = () => {
                                        attempts++;
                                        const elem = document.getElementById(targetId) || document.querySelector(`[data-search-item-id="${id}"]`);
                                        if (elem) {
                                            elem.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                        } else if (attempts < 10) {
                                            setTimeout(tryScroll, 100);
                                        }
                                    };
                                    tryScroll();
                                }
                            }
                        } catch (e) { /* no-op */ }
                    });
                }
            }
        }
    },
    created() {
        this.initializeFromUrl();
    },
    methods: {
        showLoader,
        hideLoader,
        getCurrentFilterSignature() {
            const q = this.$route.query || {};
            const qp = {};
            if (q.keyword) qp.keyword = q.keyword;
            return JSON.stringify(qp);
        },
        updateUrl() {
            const queryParams = {};

            // Add search keyword
            if (this.searchWord && this.searchWord.trim()) {
                queryParams.keyword = encodeURIComponent(this.searchWord.trim());
            }

            this.$router.replace({
                query: {
                    ...queryParams
                }
            });
        },
        initializeFromUrl() {
            const query = this.$route.query;

            // Initialize search word from URL
            if (query.keyword) {
                const searchTerm = decodeURIComponent(query.keyword);
                this.tempSearchWord = searchTerm;
                this.searchWord = searchTerm;
                this.hasResults = 0;
                this.searchResultsCounter = 0;
                this.isSearching = true;
            }
        },
        resetSearch() {
            this.tempSearchWord = '';
            this.searchWord = '';
            this.hasResults = 0;
            this.searchResultsCounter = 0;
            this.isSearching = false;

            // Clear URL parameters
            this.$router.replace({
                query: {}
            });
        },
        handleSearch() {
            if (!this.tempSearchWord.trim()) {
                this.searchWord = '';
                this.hasResults = 0;
                this.searchResultsCounter = 0;
                this.isSearching = false;
                // Clear URL if search is empty
                this.$router.replace({
                    query: {}
                });
                return;
            }
            this.searchWord = this.tempSearchWord;
            this.hasResults = 0;
            this.searchResultsCounter = 0;
            this.isSearching = true;

            // Update URL with search term
            this.updateUrl();
        },
        handleSearchResults(results) {
            this.hasResults = this.hasResults += results;
            this.searchResultsCounter++;
        }
    },
}
</script>

<style lang="scss" scoped>
@import '@/scss/_variables.scss';

.page-wrapper {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.search-container {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    background-color: #fff;
}

.footer-space {
    flex-shrink: 0;
    height: 100px;
}

.search {
    position: relative;
    z-index: 2;
    padding: 105px 0 60px;
    border-bottom-left-radius: 30px;
    border-bottom-right-radius: 30px;
    box-shadow: 0px 5px 10px 0px rgba($color: $primary, $alpha: 0.3);
    background-color: #fff;

    @media (max-width: 990px) {
        padding: 105px 0 30px;
    }

    &-form {
        display: flex;
        align-items: center;
        margin-left: 60px;
        gap: 40px;
        margin-top: -4px;

        @media (max-width: 990px) {
            flex-direction: column;
            align-items: stretch;
            gap: 20px;
        }

        .form-title {
            flex-shrink: 0;

            @media (max-width: 990px) {
                margin-bottom: 0;
                display: none;
            }

            h1 {
                font-size: 20px;
                margin: 0;
                line-height: 1;
            }
        }
    }

    @media (max-width: 786px) {
        padding: 95px 0 30px;
    }
}

.search-controls {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    max-width: 600px;

    @media (max-width: 786px) {
        gap: 10px;
    }
}

.form-element {
    flex: 1;
    min-width: 0;

    input {
        width: 100%;
        height: 40px;
    }

    &.search-element {
        @media (max-width: 786px) {
            margin: 0;
        }
    }
}

.search-btn {
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 16px;
    height: 40px;
    line-height: 1;
    white-space: nowrap;
    background-color: #007bff;
    color: #fff;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #0056b3;
    }

    .material-icons {
        font-size: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (min-width: 786px) {
            display: none !important;
        }
    }

    @media (max-width: 786px) {
        padding: 8px 12px;
        border-radius: 100px;
        height: 32px;
        width: 32px;
    }
}

.reset-btn {
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px 16px;
    height: 40px;
    line-height: 1;
    white-space: nowrap;
    background-color: #6c757d;
    color: #fff;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: #545b62;
    }

    .material-icons {
        font-size: 20px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (min-width: 786px) {
            display: none !important;
        }
    }

    @media (max-width: 786px) {
        padding: 8px 12px;
        border-radius: 100px;
        height: 32px;
        width: 32px;
    }
}

.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 40px 0;
}

.blank {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    margin-bottom: 100px;

    &-section {
        max-width: 250px;
        text-align: center;

        p {
            font-size: 16px;
            line-height: 24px;
            margin-top: 20px;
        }
    }
}

.search-img {
    @media(max-width: 560px) {
        display: none;
    }
}

.highlight {
    font-weight: bold;
    color: $primary;
}

.results-container {
    flex: 1;
    padding: 0 0 100px;
}
</style>
